import { useState, useEffect, useCallback } from 'react'
import { Send, Search, X, AlertTriangle } from 'lucide-react'
import { sendNotification } from '../services/notificationService.js'
import { getUsers } from '../services/userService.js'
import { useToast } from '../components/Toast.jsx'
import FormField from '../components/FormField.jsx'
import ConfirmDialog from '../components/ConfirmDialog.jsx'
import { validateNotificationForm, hasErrors } from '../utils/validation.js'

const inputCls = 'w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'

const CHANNEL_OPTIONS = ['PUSH', 'EMAIL', 'IN_APP']
const TYPE_OPTIONS = ['CUSTOM', 'SYSTEM_MAINTENANCE', 'FEATURE_ANNOUNCEMENT', 'PROMOTIONAL']

const NotificationsPage = () => {
  const toast = useToast()

  const [targetType, setTargetType] = useState('ALL')
  const [selectedUsers, setSelectedUsers] = useState([])
  const [userSearch, setUserSearch] = useState('')
  const [userSearchResults, setUserSearchResults] = useState([])
  const [userSearchLoading, setUserSearchLoading] = useState(false)

  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [type, setType] = useState('CUSTOM')
  const [channels, setChannels] = useState(new Set(['PUSH']))

  const [formErrors, setFormErrors] = useState({})
  const [sending, setSending] = useState(false)
  const [confirmSend, setConfirmSend] = useState(false)

  // User search with debounce
  useEffect(() => {
    if (targetType === 'ALL' || !userSearch.trim()) {
      setUserSearchResults([])
      return
    }
    const timer = setTimeout(() => {
      setUserSearchLoading(true)
      getUsers(0, 10, userSearch)
        .then((res) => setUserSearchResults(res.data.content || []))
        .catch(() => setUserSearchResults([]))
        .finally(() => setUserSearchLoading(false))
    }, 400)
    return () => clearTimeout(timer)
  }, [userSearch, targetType])

  const toggleChannel = useCallback((ch) => {
    setChannels(prev => {
      const next = new Set(prev)
      if (next.has(ch)) next.delete(ch)
      else next.add(ch)
      return next
    })
    setFormErrors(prev => ({ ...prev, channels: undefined }))
  }, [])

  const addUser = useCallback((user) => {
    setSelectedUsers(prev => {
      if (prev.find(u => u.auth0UserId === user.auth0UserId)) return prev
      return [...prev, user]
    })
    setUserSearch('')
    setUserSearchResults([])
    setFormErrors(prev => ({ ...prev, userIds: undefined }))
  }, [])

  const removeUser = useCallback((auth0UserId) => {
    setSelectedUsers(prev => prev.filter(u => u.auth0UserId !== auth0UserId))
  }, [])

  const handleSend = () => {
    const form = {
      targetType,
      userIds: selectedUsers.map(u => u.id),
      title,
      message,
      type,
      channels: Array.from(channels),
    }
    const errors = validateNotificationForm(form)
    if (hasErrors(errors)) {
      setFormErrors(errors)
      return
    }
    setConfirmSend(true)
  }

  const doSend = async () => {
    setConfirmSend(false)
    setSending(true)
    try {
      const payload = {
        targetType: targetType === 'ALL' ? 'ALL' : (selectedUsers.length === 1 ? 'USER' : 'USERS'),
        title,
        message,
        type,
        channels: Array.from(channels),
      }
      if (targetType !== 'ALL') {
        // Backend (admin-api + notification service) keys users by internal
        // UUID — device registrations and email lookups both resolve by it,
        // so Auth0 subjects must never be sent here.
        payload.userIds = selectedUsers.map(u => u.id)
      }
      const res = await sendNotification(payload)
      toast.success(`Notification queued to ${res.data.recipientCount} recipients`)
      // Reset form
      setTitle('')
      setMessage('')
      setType('CUSTOM')
      setChannels(new Set(['PUSH']))
      setSelectedUsers([])
      setTargetType('ALL')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send notification')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold">Send Notification</h1>
        <p className="text-sm text-gray-400 mt-1">Send push notifications, emails, or in-app messages to users</p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 space-y-6">
        {/* Recipients */}
        <div>
          <label className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 block">Recipients</label>
          <div className="flex gap-3 mb-3">
            <button onClick={() => { setTargetType('ALL'); setSelectedUsers([]) }}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${targetType === 'ALL' ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
              All Users
            </button>
            <button onClick={() => setTargetType('SPECIFIC')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${targetType === 'SPECIFIC' ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
              Specific Users
            </button>
          </div>

          {targetType === 'SPECIFIC' && (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" placeholder="Search users by name or email..."
                  value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                  className={`${inputCls} pl-10`} />
              </div>

              {userSearchLoading && <p className="text-xs text-gray-400">Searching...</p>}

              {userSearchResults.length > 0 && (
                <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-xl">
                  {userSearchResults.map((u) => (
                    <button key={u.id} onClick={() => addUser(u)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left border-b border-gray-100 dark:border-gray-700/50 last:border-0 text-sm">
                      <div className="w-7 h-7 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                        {(u.name?.[0] || u.email?.[0] || '?').toUpperCase()}
                      </div>
                      <span className="font-medium">{u.name || 'No name'}</span>
                      <span className="text-gray-400 text-xs">{u.email}</span>
                    </button>
                  ))}
                </div>
              )}

              {selectedUsers.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedUsers.map((u) => (
                    <span key={u.auth0UserId} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-full text-sm text-blue-400">
                      {u.name || u.email}
                      <button onClick={() => removeUser(u.auth0UserId)} className="hover:text-red-400 transition-colors" aria-label={`Remove ${u.name || u.email}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {formErrors.userIds && <p className="text-xs text-red-400">{formErrors.userIds}</p>}
            </div>
          )}
        </div>

        {/* Title */}
        <FormField label="Title" htmlFor="notif-title" error={formErrors.title}>
          <input id="notif-title" type="text" placeholder="Notification title..."
            value={title} onChange={(e) => { setTitle(e.target.value); setFormErrors(f => ({ ...f, title: undefined })) }}
            className={inputCls} />
        </FormField>

        {/* Message */}
        <FormField label="Message" htmlFor="notif-message" error={formErrors.message}>
          <textarea id="notif-message" placeholder="Notification message..."
            value={message} onChange={(e) => { setMessage(e.target.value); setFormErrors(f => ({ ...f, message: undefined })) }}
            rows={4} className={`${inputCls} resize-none`} />
        </FormField>

        {/* Type */}
        <FormField label="Type" htmlFor="notif-type">
          <select id="notif-type" value={type} onChange={(e) => setType(e.target.value)} className={inputCls}>
            {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </select>
        </FormField>

        {/* Channels */}
        <div>
          <label className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 block">Channels</label>
          <div className="flex gap-3">
            {CHANNEL_OPTIONS.map((ch) => (
              <button key={ch} onClick={() => toggleChannel(ch)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border ${channels.has(ch) ? 'bg-blue-500/20 border-blue-500/50 text-blue-400' : 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                {ch.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
          {formErrors.channels && <p className="text-xs text-red-400 mt-1">{formErrors.channels}</p>}
        </div>

        {/* Send */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <button onClick={handleSend} disabled={sending}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 text-sm w-full justify-center">
            <Send className="w-4 h-4" />
            {sending ? 'Sending...' : 'Send Notification'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmSend}
        title="Send Notification"
        message={`Are you sure you want to send "${title}" to ${targetType === 'ALL' ? 'all users' : `${selectedUsers.length} selected user(s)`} via ${Array.from(channels).join(', ')}?`}
        confirmLabel="Send"
        onConfirm={doSend}
        onCancel={() => setConfirmSend(false)}
      />
    </div>
  )
}

export default NotificationsPage
